/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';

import { useRequester } from '../../hooks';
import { UserData, UserResource } from '../../resources';
import { useEffect, useMemo, useState, useContext } from 'react';
import { FileDownload } from '@mui/icons-material';
import { Menu, Table, Page, Searchbar } from '../../design';
import { AuthContext } from '../../contexts';
import { formatDate } from '../../utils';
import { AppScope, SCOPE_LABEL_MAP } from '../../resources/BaseResource';
import moment from 'moment';

const today = moment().endOf('day').toDate();
const weekAgo = moment().startOf('day').subtract(7, 'days').toDate();

function downloadAsCsv(columns: any[], rows: any[]) {
  let csv = 'data:text/csv;charset=utf-8,';
  for (const column of columns) csv += column.label + ',';

  csv += '\n';
  for (const row of rows) {
    for (const column of columns) csv += (row[column.id] || 0) + ',';
    csv += '\n';
  }

  const encodedUri = encodeURI(csv);
  const link = document.createElement('a');
  link.setAttribute('href', encodedUri);
  link.setAttribute('download', 'activities.csv');

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

const makeColumns = (from: Date, to: Date) => {
  const columns = [
    { id: 'email', label: 'Email', align: 'center' as const },
    { id: 'access', label: 'Access', align: 'center' as const },
    {
      id: 'createdAt',
      label: 'Joined on',
      align: 'center' as const,
      format: (value: any) => formatDate(value),
    },
  ];
  for (let d = from; d < to; d.setDate(d.getDate() + 1)) {
    columns.push({
      id: moment(d).format('YYYY-MM-DD'),
      label: moment(d).format('YYYY-MM-DD'),
      align: 'center' as const,
    });
  }
  return columns;
};

const makeRows = (from: Date, to: Date, users: UserData[], data: any[]) => {
  const dates = new Set<string>();
  for (let d = from; d < to; d.setDate(d.getDate() + 1)) {
    dates.add(d.toISOString().slice(0, 10));
  }

  const map = new Map<string, Map<string, string>>();
  const emails = new Set<string>();
  for (const entry of users) {
    emails.add(entry.email);
    map.set(
      entry.email,
      new Map<string, string>([
        ['createdAt', entry.createdAt],
        ['access', entry.access.join('/')],
      ])
    );
  }

  for (const entry of data) {
    if (!map.has(entry.email)) {
      map.set(entry.email, new Map<string, string>());
    }
    map.get(entry.email)?.set(entry.date, entry.metric);
  }

  const rows: any[] = [];

  for (const email of Array.from(emails).sort()) {
    const row: any = { id: email, email };
    row.createdAt = map.get(email)?.get('createdAt') || '';
    row.access = map.get(email)?.get('access') || '';
    for (const date of Array.from(dates)) {
      row[date] = map.get(email)?.get(date) || '';
    }
    rows.push(row);
  }

  return rows;
};

type Query = {
  offset: number;
  limit: number;
  qs: string;
};

export default function ActivitiesPage() {
  const { user } = useContext(AuthContext);
  const [hasReachedBottom, setHasReachedBottom] = useState(false);
  const [query, setQuery] = useState<Query>({ offset: 0, limit: 50, qs: '' });

  const [downloading, setDownloading] = useState(false);
  const [activities, setActivities] = useState<any[]>([]);
  const [filter, setFilter] = useState<any>({
    from: moment(weekAgo).startOf('day').toDate(),
    to: moment(today).endOf('day').toDate(),
    type: 'app_usage',
  });

  const activitiesApi = useRequester<any>({
    onSubmit: (q: any) => UserResource.getAllUsersActivities(q),
    onSuccess: setActivities,
    onError: console.log,
  });

  const usersApi = useRequester<UserResource[]>({
    onSubmit: (q) => UserResource.fetchMany(q),
    onSuccess: (data) => setHasReachedBottom(data.length < query.limit),
    onError: console.log,
  });

  useEffect(() => {
    usersApi.submit(query);
  }, [query]);

  useEffect(() => {
    activitiesApi.submit({ groupBy: 'date', ...filter });
  }, [filter]);

  const columns = useMemo(() => makeColumns(new Date(filter.from), new Date(filter.to)), [filter]);

  const downloadData = async (q: { guestOnly?: boolean; excludeGuest?: boolean }) => {
    setDownloading(true);
    const users = await UserResource.fetchAll(q);

    const rows = makeRows(new Date(filter.from), new Date(filter.to), users, activities);
    downloadAsCsv(columns, rows);
    setDownloading(false);
  };

  const rows = makeRows(
    new Date(filter.from),
    new Date(filter.to),
    UserResource.list().map((u) => u.data),
    activities
  );

  return (
    <Page>
      <Page.Header>
        <Box className="flex flex-col h-full">
          <Typography variant="body1" color="text.secondary" sx={{ mt: 8 }}>
            👋 Hey, {user?.data.firstName}!
          </Typography>
          <Typography variant="h1">
            {SCOPE_LABEL_MAP[UserResource.scope as AppScope]} User Activities
          </Typography>
          <Box className="flex-grow"></Box>
        </Box>
      </Page.Header>
      <Page.Content>
        <Box className="flex justify-between mt-8 mb-8">
          <Box className="flex gap-4">
            <Searchbar
              placeholder="Search for a student"
              onChange={(qs) => setQuery({ ...query, qs })}
            />
            {(usersApi.loading || activitiesApi.loading) && (
              <Box className="flex items-center">
                <CircularProgress size={28} />
              </Box>
            )}
          </Box>

          <Box className="flex gap-4">
            <TextField
              label="From"
              InputLabelProps={{ shrink: true }}
              type="date"
              defaultValue={moment(weekAgo).format('YYYY-MM-DD')}
              onChange={(e) => {
                setFilter({
                  ...filter,
                  from: moment(e.target.value).startOf('day').toDate(),
                });
              }}
            />
            <TextField
              label="To"
              InputLabelProps={{ shrink: true }}
              type="date"
              defaultValue={moment(today).format('YYYY-MM-DD')}
              onChange={(e) => {
                setFilter({
                  ...filter,
                  to: moment(e.target.value).endOf('day').toDate(),
                });
              }}
            />

            <Select
              defaultValue="app_usage"
              onChange={(e) => {
                setFilter({ ...filter, type: e.target.value });
              }}
            >
              <MenuItem value="app_usage">App Usage Time</MenuItem>
              <MenuItem value="speaking_time">Speaking Time</MenuItem>
              <MenuItem value="lesson_count">Lesson Count</MenuItem>
              <MenuItem value="article_read">Article Read</MenuItem>
            </Select>

            <Menu
              anchor={
                <IconButton disabled={downloading} sx={{ aspectRatio: 1 }}>
                  {downloading ? (
                    <CircularProgress size={28} />
                  ) : (
                    <FileDownload sx={{ fontSize: 28 }} />
                  )}
                </IconButton>
              }
              items={[
                {
                  name: 'all',
                  label: 'All',
                  onSelect: () => downloadData({}),
                },
                {
                  name: 'guests',
                  label: 'Guests Only',
                  onSelect: () => downloadData({ guestOnly: true }),
                },
                {
                  name: 'noguests',
                  label: 'Exclude Guests',
                  onSelect: () => downloadData({ excludeGuest: true }),
                },
              ]}
            />
          </Box>
        </Box>

        <Table rows={rows} columns={columns} />

        {!hasReachedBottom && rows.length > 0 && (
          <Button
            disabled={usersApi.loading}
            sx={{ mt: 2 }}
            fullWidth
            onClick={() => setQuery({ ...query, offset: query.offset + query.limit })}
          >
            Load More
          </Button>
        )}
      </Page.Content>
    </Page>
  );
}
